<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="slipCreateTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl slip-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="slipCreateTitle">
            Lieferschein an {{ recipient?.description ?? '' }}
          </h5>
          <button
            type="button"
            class="btn-close"
            :disabled="creating"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Druckerauswahl aus dem Solo, eingebettet wie im Etikettendruck. Die
               Höhe meldet das Widget selbst per postMessage — sonst wird das
               aufgeklappte Dropdown abgeschnitten. -->
          <div v-if="printerEmbedUrl" class="printer-frame-wrap">
            <iframe
              class="printer-frame"
              title="Druckerauswahl"
              :src="printerEmbedUrl"
              :style="{ height: printerFrameHeight + 'px' }"
            ></iframe>
          </div>
          <div v-else-if="printerError" class="error">{{ printerError }}</div>
          <div v-else class="spinner" aria-label="Lädt"></div>

          <div v-if="loading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="loadError" class="error">{{ loadError }}</div>
          <div v-else-if="candidates.length === 0" class="error">
            Für diesen Servicepartner ist kein Gerät mehr vorbereitet.
          </div>

          <template v-else>
            <!-- Gegenkontrolle vor dem Abschluss: jedes Gerät, das mitgeht, muss
                 einmal in der Hand gewesen sein. Der Handscanner schickt die
                 R2-Nummer plus Enter, der Cursor bleibt stehen. -->
            <div class="slip-scan">
              <label for="slip-scan-input" class="form-label">R2-Nummer scannen</label>
              <input
                id="slip-scan-input"
                ref="scanInput"
                v-model="scanValue"
                type="text"
                class="form-control"
                placeholder="R2-xxxx"
                autocomplete="off"
                :disabled="creating"
                @keydown.enter.prevent="onScan"
              />
              <div class="slip-scan-progress" :class="{ 'slip-scan-done': allScanned }">
                {{ scannedIds.size }} von {{ candidates.length }} abgescannt
                <template v-if="allScanned"> — vollständig</template>
              </div>
              <div v-if="scanError" class="error slip-scan-msg">{{ scanError }}</div>
              <div v-else-if="scanNotice" class="slip-scan-msg slip-scan-notice">{{ scanNotice }}</div>
            </div>

            <div class="table-responsive slip-table-wrap slip-table-nowrap">
              <table class="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th scope="col" class="slip-check-cell"><span class="visually-hidden">Abgescannt</span></th>
                    <th scope="col">R2-Nr</th>
                    <!-- Hersteller, Gerät, Farbe und Speicher in einer Spalte: mit
                         Scan-Haken und „Nicht mitschicken" wurde die Tabelle sonst zu breit.
                         Der Text ist derselbe wie auf dem Lieferschein. -->
                    <th scope="col">Gerät</th>
                    <th scope="col">IMEI / SN</th>
                    <th scope="col">Fehlerbild</th>
                    <th scope="col" class="slip-price-cell">Preis</th>
                    <th scope="col"><span class="visually-hidden">Nicht mitschicken</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="c in candidates"
                    :key="c.stock_device_id"
                    :class="{
                      'slip-row-scanned': scannedIds.has(c.stock_device_id),
                      'slip-row-last':    c.stock_device_id === lastScannedId,
                    }"
                  >
                    <td class="slip-check-cell">{{ scannedIds.has(c.stock_device_id) ? '✓' : '' }}</td>
                    <td class="ticket-mono">{{ c.stock_number }}</td>
                    <td>{{ c.description }}</td>
                    <td class="ticket-mono">{{ c.imei_sn || '—' }}</td>
                    <td>{{ c.error_pattern || '—' }}</td>
                    <td class="slip-price-cell">{{ c.error_pattern_price_label || '—' }}</td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="creating"
                        :title="`${c.stock_number} nicht mitschicken`"
                        @click="onRemove(c)"
                      >Nicht mitschicken</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <div v-if="creating" class="print-status">
            <div class="spinner" aria-label="Erstellt"></div>
            <span>Der Lieferschein wird erstellt und gedruckt…</span>
          </div>
          <div v-else-if="errorMessage" class="error print-error">{{ errorMessage }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="creating" data-bs-dismiss="modal">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="creating || !allScanned"
            :title="allScanned ? '' : 'Erst alle Geräte abscannen'"
            @click="onCreate"
          >{{ creating ? 'Erstellt …' : 'Jetzt erstellen' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  createDeliverySlip,
  listCandidates,
  type DeliverySlipCandidate,
  type DeliverySlipRecipient,
  type DeliverySlipResult,
} from '../services/deliverySlips';
import { parseStockNumber } from '../services/stockDevices';
import { getPeppiPrinterUrls } from '../services/me';

/**
 * Die Fälle, die mit dem Lieferschein hinausgehen, plus Druckerauswahl.
 *
 * Vor dem Abschluss muss jedes Gerät einmal abgescannt werden — „Jetzt erstellen"
 * bleibt gesperrt, bis die Liste vollständig ist. Das ist der Zweck des Modals:
 * es prüft, dass die Geräte auch wirklich im Paket liegen.
 *
 * Ein Gerät, das gerade nicht mitgeht, nimmt man über „Nicht mitschicken" aus
 * DIESEM Lieferschein — sein Versand bleibt vorbereitet und kommt mit dem
 * nächsten mit. Das ist bewusst KEIN Storno: der Versand wird verschoben, nicht
 * aufgehoben. Storniert wird über „Versand stornieren" am Gerät selbst.
 * Deshalb passiert hier auch kein Serveraufruf — die Auswahl geht erst beim
 * Erstellen mit.
 *
 * „Jetzt erstellen" legt den Lieferschein an und schickt das A4-PDF an die
 * Druckstation. Druckt die nicht, ist das KEIN Fehler: der Lieferschein gilt, das
 * Papier liegt in der Warteschlange. Die Meldung dazu holt der Aufrufer aus dem
 * Ergebnis (`printed` / `print_warning`).
 *
 * `open(recipient)` liefert den erstellten Lieferschein oder null.
 */
const modalEl   = useTemplateRef<HTMLDivElement>('modalEl');
const scanInput = useTemplateRef<HTMLInputElement>('scanInput');
let   modal: Modal | null = null;
let   resolver: ((result: DeliverySlipResult | null) => void) | null = null;
let   created: DeliverySlipResult | null = null;

// Bootstrap verwirft ein hide() während der Show-Transition — dieselbe Falle wie
// im Etikettendruck. Daher: hide nur, wenn „shown“ durch ist, sonst vormerken.
let isShown  = false;
let wantHide = false;

const recipient    = ref<DeliverySlipRecipient | null>(null);
const candidates   = ref<DeliverySlipCandidate[]>([]);
const loading      = ref(false);
const loadError    = ref<string | null>(null);
const creating     = ref(false);
const errorMessage = ref<string | null>(null);

const scanValue     = ref('');
const scanError     = ref<string | null>(null);
const scanNotice    = ref<string | null>(null);
const scannedIds    = ref<Set<number>>(new Set());
const lastScannedId = ref<number | null>(null);

const printerEmbedUrl    = ref<string | null>(null);
const printerError       = ref<string | null>(null);
const printerFrameHeight = ref(60);

// Ohne Geräte gibt es nichts zu bestätigen — dann bleibt der Knopf ebenfalls aus.
const allScanned = computed<boolean>(
  () => candidates.value.length > 0 && scannedIds.value.size === candidates.value.length,
);

onMounted(() => {
  window.addEventListener('message', onPrinterFrameMessage);
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('shown.bs.modal', onShown);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onPrinterFrameMessage);
  modalEl.value?.removeEventListener('shown.bs.modal', onShown);
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function onPrinterFrameMessage(event: MessageEvent): void {
  if (printerEmbedUrl.value === null) return;
  if (event.origin !== new URL(printerEmbedUrl.value).origin) return;
  const { type, height } = event.data ?? {};
  if (type === 'PEPPI_PRINTER_HEIGHT' && typeof height === 'number') {
    printerFrameHeight.value = Math.max(40, Math.min(height, 500));
  }
}

function open(chosen: DeliverySlipRecipient): Promise<DeliverySlipResult | null> {
  recipient.value     = chosen;
  candidates.value    = [];
  created             = null;
  loading.value       = true;
  loadError.value     = null;
  creating.value      = false;
  errorMessage.value  = null;
  scanValue.value     = '';
  scanError.value     = null;
  scanNotice.value    = null;
  scannedIds.value    = new Set();
  lastScannedId.value = null;
  isShown             = false;
  wantHide            = false;
  modal?.show();
  void loadCandidates();
  void loadPrinterUrl();
  return new Promise<DeliverySlipResult | null>(resolve => {
    resolver = resolve;
  });
}

async function loadCandidates(): Promise<void> {
  if (recipient.value === null) return;
  try {
    candidates.value = await listCandidates(recipient.value.id);
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Die Geräte konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function loadPrinterUrl(): Promise<void> {
  if (printerEmbedUrl.value !== null) return;
  printerError.value = null;
  try {
    printerEmbedUrl.value = (await getPeppiPrinterUrls()).embedUrl;
  } catch (e) {
    printerError.value = e instanceof Error
      ? `Druckerauswahl konnte nicht geladen werden: ${e.message}`
      : 'Druckerauswahl konnte nicht geladen werden.';
  }
}

/**
 * Gescannte Nummer gegen die Liste halten. Ein zweiter Scan desselben Geräts ist
 * kein Fehler, sondern eine Rückmeldung — beim Packen scannt man leicht doppelt.
 */
function onScan(): void {
  scanError.value  = null;
  scanNotice.value = null;
  const stockNumber = parseStockNumber(scanValue.value);
  scanValue.value   = '';
  void nextTick(() => scanInput.value?.focus());
  if (stockNumber === null) return;

  const hit = candidates.value.find(c => c.stock_number === stockNumber);
  if (!hit) {
    scanError.value = `${stockNumber} gehört nicht zu diesem Lieferschein.`;
    return;
  }
  lastScannedId.value = hit.stock_device_id;
  if (scannedIds.value.has(hit.stock_device_id)) {
    scanNotice.value = `${stockNumber} war schon abgescannt.`;
    return;
  }
  scannedIds.value = new Set(scannedIds.value).add(hit.stock_device_id);
  scanNotice.value = `${stockNumber} bestätigt.`;
}

/**
 * Nimmt ein Gerät aus DIESEM Lieferschein. Kein Serveraufruf: der Versand bleibt
 * vorbereitet und kommt mit dem nächsten Lieferschein mit — verschoben, nicht
 * storniert. Was mitgeht, entscheidet erst „Jetzt erstellen".
 */
function onRemove(candidate: DeliverySlipCandidate): void {
  if (creating.value) return;
  errorMessage.value = null;
  candidates.value = candidates.value.filter(c => c.stock_device_id !== candidate.stock_device_id);
  // Auch aus den Scans nehmen, sonst zählt ein herausgenommenes Gerät weiter mit.
  const scanned = new Set(scannedIds.value);
  scanned.delete(candidate.stock_device_id);
  scannedIds.value = scanned;
}

async function onCreate(): Promise<void> {
  if (creating.value || recipient.value === null || !allScanned.value) return;
  creating.value     = true;
  errorMessage.value = null;
  try {
    created = await createDeliverySlip(
      recipient.value.id,
      candidates.value.map(c => c.stock_device_id),
    );
    requestHide();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Der Lieferschein konnte nicht erstellt werden.';
  } finally {
    creating.value = false;
  }
}

function onShown(): void {
  isShown = true;
  if (wantHide) {
    wantHide = false;
    modal?.hide();
    return;
  }
  scanInput.value?.focus();
}

function requestHide(): void {
  if (isShown) modal?.hide();
  else wantHide = true;
}

function onHidden(): void {
  isShown = false;
  resolver?.(created);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.printer-frame-wrap {
  margin-bottom: 0.75rem;
}

.printer-frame {
  width: 100%;
  border: 0;
}

.slip-scan {
  margin-bottom: 0.75rem;
}

.slip-scan-progress {
  margin-top: 0.35rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #6c757d;
}

.slip-scan-done {
  color: #198754;
}

.slip-scan-msg {
  margin-top: 0.2rem;
  font-size: 0.9rem;
}

.slip-scan-notice {
  color: #6c757d;
}

/* Breiter als Bootstraps modal-xl (1140px): eine Zeile je Gerät soll ohne Umbruch
   hineinpassen. 96vw laesst am Rand Luft, damit der Backdrop noch sichtbar bleibt. */
.slip-dialog {
  max-width: min(1700px, 96vw);
}

.slip-table-wrap {
  max-height: 45vh;
  overflow-y: auto;
}

/* Kein Umbruch in den Zellen — ein Gerät ist eine Zeile. Passt es trotz der Breite
   nicht, scrollt die Tabelle waagrecht (table-responsive), statt Zeilen zu stapeln. */
.slip-table-nowrap :deep(th),
.slip-table-nowrap :deep(td) {
  white-space: nowrap;
}

.slip-check-cell {
  width: 2rem;
  text-align: center;
  color: #198754;
  font-weight: 700;
}

.slip-row-scanned {
  background: rgba(25, 135, 84, 0.08);
}

.slip-row-last {
  outline: 2px solid rgba(25, 135, 84, 0.55);
  outline-offset: -2px;
}

.slip-price-cell {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.print-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.print-error {
  margin-top: 0.75rem;
}

.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #ced4da;
  border-top-color: #0d6efd;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}

.ticket-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
</style>
